/**
 * The client for the Cloud API.
 *
 * Two things it does that a generated client would not, and both are about how
 * failure reaches the interface.
 *
 * **A failure carries what the interface needs to act.** The API answers with a
 * message for the user, a hint, and a `code` for the application — so
 * `no-workspace` becomes the "what is your business called" screen rather than an
 * error, and `limit-reached` becomes an upgrade rather than a retry button. Losing
 * the code and keeping only the message is how a product ends up showing somebody
 * a red box where it should have shown them a question.
 *
 * **A fresh token per call.** Clerk's session tokens are deliberately short-lived,
 * and an application left open over lunch would otherwise start failing on the
 * first click after it. The account page on the marketing site does the same.
 */
import type { ChangeKind, DataSource, ReportData, ReportTemplate, TemplateDiff, Theme } from "@broadpaper/core";
import type { TemplateAiRequest } from "@broadpaper/core/ai";
import type { Choice, Confidence, Mapping } from "@broadpaper/mapping";
import type { SourceColumn } from "@broadpaper/ingest";
import type { Mark } from "./ui/index.js";
import { session } from "./clerk.js";

const BASE = ((import.meta.env.VITE_CLOUD_API as string | undefined) || "http://localhost:4790").replace(/\/$/, "");

/** Where the marketing site is, for help pages. */
const SITE = ((import.meta.env.VITE_SITE_URL as string | undefined) || "https://www.broadpaper.com").replace(/\/$/, "");

/**
 * A support page, and a section of it.
 *
 * Written out as `/support/<page>/#<section>` in each call so that
 * `tests/site/support.test.ts` can find every one in this application's source and
 * check the page and the section exist — a help link that lands on the top of the
 * wrong page is worse than no link, because it looks as if it should have helped.
 */
export function helpUrl(path: `/support/${string}`): string {
  return `${SITE}${path}`;
}

/**
 * One of the policies on the marketing site. Written out in full for the same
 * reason as `helpUrl`: `tests/site/support.test.ts` checks every one names a
 * policy that exists, and that sign-in links the two Google's review looks for.
 */
export function legalUrl(path: `/legal/${"terms" | "privacy" | "cookies" | "refunds"}/`): string {
  return `${SITE}${path}`;
}

/** The marketing site itself, for the way back out of the application. */
export function siteUrl(path: "/" | "/pricing/" = "/"): string {
  return `${SITE}${path}`;
}

export type FailureCode =
  | "no-workspace"
  | "not-signed-in"
  | "not-allowed"
  | "not-found"
  | "bad-request"
  | "too-large"
  | "limit-reached"
  | "rate-limited"
  | "conflict"
  | "unavailable"
  | "server-error"
  /** The design assistant could not produce a design that passes the checks. */
  | "unprocessable"
  | "offline";

export class ApiError extends Error {
  constructor(
    readonly code: FailureCode,
    message: string,
    readonly hint?: string,
    readonly detail?: Record<string, unknown>,
    readonly status = 0
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const current = session();
  if (!current) throw new ApiError("not-signed-in", "You are not signed in.", "Sign in again and you will be back where you were.");
  const token = await current.getToken();

  let response: Response;
  try {
    response = await fetch(`${BASE}/${path.replace(/^\//, "")}`, {
      ...init,
      headers: {
        ...(init.headers ?? {}),
        authorization: `Bearer ${token}`,
        ...(init.body ? { "content-type": "application/json" } : {})
      }
    });
  } catch (e) {
    // Cancelled by the person, not lost: the caller asked for this and must hear
    // it as a cancellation, not as the wifi.
    if (init.signal?.aborted) throw e;
    // A fetch that rejects is the network, not the service. Saying so is the
    // difference between a user checking their wifi and a user emailing support.
    throw new ApiError("offline", "BroadPaper couldn't reach the internet.", "Check your connection — nothing you have done has been lost.");
  }

  const body = (await response.json().catch(() => ({}))) as { error?: string; code?: FailureCode; hint?: string; detail?: Record<string, unknown> };
  if (!response.ok) {
    throw new ApiError(body.code ?? "server-error", body.error ?? "Something went wrong.", body.hint, body.detail, response.status);
  }
  return body as T;
}

/**
 * A call nobody is signed in for: the share page.
 *
 * Kept apart from `call` rather than making the token optional there, because a
 * signed-in call that quietly went out without its token would be refused with a
 * message about signing in — on a screen where the person *is* signed in.
 */
async function anonymous<T>(path: string, body?: unknown): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${BASE}/${path.replace(/^\//, "")}`, {
      method: body === undefined ? "GET" : "POST",
      ...(body === undefined ? {} : { headers: { "content-type": "application/json" }, body: JSON.stringify(body) })
    });
  } catch {
    throw new ApiError("offline", "BroadPaper couldn't reach the internet.", "Check your connection and try again.");
  }
  const parsed = (await response.json().catch(() => ({}))) as { error?: string; code?: FailureCode; hint?: string; detail?: Record<string, unknown> };
  if (!response.ok) throw new ApiError(parsed.code ?? "server-error", parsed.error ?? "Something went wrong.", parsed.hint, parsed.detail, response.status);
  return parsed as T;
}

const get = <T>(path: string) => call<T>(path);
const post = <T>(path: string, body?: unknown) => call<T>(path, { method: "POST", ...(body === undefined ? {} : { body: JSON.stringify(body) }) });
const put = <T>(path: string, body: unknown) => call<T>(path, { method: "PUT", body: JSON.stringify(body) });
const patch = <T>(path: string, body: unknown) => call<T>(path, { method: "PATCH", body: JSON.stringify(body) });
const del = <T>(path: string) => call<T>(path, { method: "DELETE" });

/* ---------------------------------- shapes --------------------------------- */

export interface Limits {
  datasets: number;
  templates: number;
  reportsPerMonth: number;
  storageBytes: number;
  rowsPerDataset: number;
  uploadBytes: number;
  /** Word documents and PDFs made into designs, a month. Zero means the plan does not import. */
  importsPerMonth: number;
  /** People who can belong to the workspace. Clerk enforces it; this is for saying so. */
  members: number;
}

/** An amount in minor units, as Creem gives it: 5900 is $59.00. */
export interface Money {
  amount: number;
  currency: string;
}

export interface WorkspaceView {
  workspace: { id: string; name: string; createdAt: string };
  role: string;
  plan: {
    id: string;
    name: string;
    marked: boolean;
    /** Sold through Creem. The free plan is the one that is not. */
    paid: boolean;
    status: string | null;
    renewsAt: string | null;
    cancelAtPeriodEnd: boolean;
    inGrace: boolean;
  };
  limits: Limits;
  usage: { datasets: number; templates: number; reportsThisMonth: number; storageBytes: number; importsThisMonth: number };
  /** Prices are Creem's, read by the service, and null when it could not ask. */
  plans: Array<{ id: string; name: string; paid: boolean; monthly: Money | null; annual: Money | null; marked: boolean; limits: Limits; current: boolean }>;
}

/**
 * The Google Drive or OneDrive file a set of data is refreshed from.
 *
 * An address and nothing that opens it: the file is read again in this tab, with a
 * sign-in this tab holds. `sheet` is the workbook tab that was chosen.
 */
export interface DatasetLink {
  provider: "google-drive" | "onedrive";
  fileId: string;
  driveId?: string;
  resourceKey?: string;
  name: string;
  sheet?: string;
}

export interface DatasetSummary {
  id: string;
  name: string;
  rowCount: number;
  bytes: number;
  /** csv, tsv, xlsx, json, ndjson or paste. For the interface, never for a decision. */
  source: string;
  folderId: string | null;
  link: DatasetLink | null;
  refreshedAt: string | null;
  createdAt: string;
  updatedAt: string;
  columns: string[];
}

/** A column as the review screen shows it: with an example formatted the way a document will. */
export type ReviewColumn = SourceColumn & { preview: string };

export interface DatasetDetail {
  dataset: { id: string; name: string; rowCount: number; source: string; link: DatasetLink | null; refreshedAt: string | null };
  columns: ReviewColumn[];
  sampleRows: Array<Record<string, unknown>>;
  dataSource: DataSource;
}

export interface StarterSummary {
  id: string;
  name: string;
  summary: string;
  wants: string;
  themeId: string;
  /** The page's shape, from shared/starter-sketches.json. Drawn by `Sketch`. */
  sketch: Mark[];
  fields: number;
}

export interface TemplateSummary {
  id: string;
  name: string;
  startedFrom: string | null;
  dataset: { id: string; name: string } | null;
  folderId: string | null;
  createdAt: string;
  updatedAt: string;
  /** The Word document or PDF it was made from, or null for a design started here. */
  importedFrom: string | null;
  /** A picture of page one, or null until one has been drawn. */
  thumbnail: { url: string; drawnAt: string } | null;
}

export type RevisionReason = "baseline" | "start" | "edit" | "restore" | "import";

/** One version of a design, as the history list shows it. */
export interface RevisionSummary {
  number: number;
  name: string;
  reason: RevisionReason;
  restoredFrom: number | null;
  createdAt: string;
  updatedAt: string;
  /** "3 changes: 1 added, 2 changed", or null for the first version. */
  summary: string | null;
  counts: Record<ChangeKind, number> | null;
  current: boolean;
}

export interface RevisionPage {
  template: { id: string; name: string };
  revisions: RevisionSummary[];
  total: number;
  current: number;
  /** Pass back as `before` for the next, older page. Null when there is none. */
  nextBefore: number | null;
}

export interface RevisionDetail {
  revision: RevisionSummary;
  json: ReportTemplate;
  compare: { mode: "previous" | "current" | "number"; number: number | null };
  /** From the compared version to this one. Null when there is nothing to compare with. */
  diff: (TemplateDiff & { summary: string }) | null;
}

export type ShareState = "active" | "expired" | "stopped";

/** A share link as its owner sees it. */
export interface ShareView {
  id: string;
  url: string;
  label: string | null;
  state: ShareState;
  hasPassword: boolean;
  expiresAt: string | null;
  stoppedAt: string | null;
  createdAt: string;
  views: number;
  downloads: number;
  visitors: number;
  passwordFailures: number;
  firstViewedAt: string | null;
  lastViewedAt: string | null;
  recent: Array<{ kind: "view" | "download" | "denied"; device: string | null; at: string }>;
}

/** What a share link shows somebody who is not signed in. */
export interface PublicShare {
  state: ShareState;
  from: string;
  needsPassword?: boolean;
  document?: SharedDocumentInfo | null;
}

export interface SharedDocumentInfo {
  name: string;
  pages: number | null;
  bytes: number | null;
  producedAt: string;
}

/** What deleting the account would delete, and whom it would affect. */
export interface ClosurePreview {
  workspace: { name: string };
  canDelete: boolean;
  counts: { datasets: number; templates: number; revisions: number; reports: number; brands: number; folders: number; shares: number; activeShares: number };
  storageBytes: number;
  subscription: { live: boolean; plan: string; status: string | null; renewsAt: string | null; cancelAtPeriodEnd: boolean };
  otherOrganisations: Array<{ id: string; name: string }> | null;
  otherMembers: number | null;
}

/** One folder in a section's tree. Flat on the wire; the sidebar assembles it. */
export interface FolderRow {
  id: string;
  /** data | designs | documents */
  section: string;
  parentId: string | null;
  name: string;
  updatedAt: string;
}

/** A business's own details, as they appear at the top of a document. */
export interface Brand {
  id: string;
  name: string;
  company: string;
  address: string;
  email: string;
  phone: string;
  website: string;
  registration: string;
  /** A data URI, or null. */
  logo: string | null;
  /** Null leaves every design with the look it came with. */
  themeId: string | null;
  colour: string | null;
  isDefault: boolean;
  updatedAt: string;
}

/** One of the four looks, described well enough to draw a swatch of it. */
export interface Look {
  id: string;
  name: string;
  colour: string;
  accent: string;
  headingFont: string;
}

export interface TemplateDetail {
  template: { id: string; name: string; starterId: string | null; updatedAt: string; importedFrom: string | null };
  json: ReportTemplate;
  dataset: { id: string; name: string; rowCount: number } | null;
  dataSources: DataSource[];
  data: ReportData;
  theme: Theme;
  /** What is currently filling each field. Null for a design with no data behind it. */
  mapping: Mapping | null;
  /** Whose letterhead it carries, and everything it could carry instead. */
  brand: { id: string; name: string } | null;
  brands: Array<{ id: string; name: string }>;
  /** Saved choices the service is no longer honouring, in words, and why. */
  dropped: Array<{ path: string; label: string; column: string; because: string }>;
  /** Fields taking one value out of a column that holds several — a sheet of many documents. */
  firstRowOnly: Array<{ path: string; label: string; column: string; distinct: number; using: unknown }>;
  /** The column this design is split by, and every document the file holds. */
  groupBy: string | null;
  groups: Array<{ key: string; label: string; rows: number }>;
  /** Which of them the canvas is showing. */
  group: string | null;
  /** What else this data could be split by, for changing it without leaving. */
  splitOptions: Array<{ column: string; label: string; groups: number }>;
  needsData: boolean;
}

/** One field the design needs, and what BroadPaper proposes to fill it with. */
export interface MatchRow {
  path: string;
  label: string;
  group: string | null;
  type: string;
  required: boolean;
  isList: boolean;
  confidence: Confidence;
  because: string;
  /** Filled in, but from something that does not really fit. Null when it does. */
  warning: string | null;
  choice: Choice;
  chosenColumn: string | null;
  example: string;
  options: Array<{ choice: Choice; label: string; because: string; example: string }>;
}

export interface MatchResult {
  summary: string;
  settled: MatchRow[];
  questions: MatchRow[];
  mapping: Mapping;
  columns: Array<{ name: string; label: string; type: string; example: string }>;
  /** Whether this file looks like several documents, and what it could be split by. */
  grouping: {
    column: string | null;
    options: Array<{ column: string; label: string; groups: number; perRow: boolean; example: string }>;
  };
  complete: boolean;
}

export interface ReportSummary {
  id: string;
  name: string;
  status: "queued" | "rendering" | "ready" | "failed";
  pages: number | null;
  bytes: number | null;
  marked: boolean;
  error: string | null;
  createdAt: string;
  readyAt: string | null;
  templateId: string;
  folderId: string | null;
  /**
   * Which file of a document too long for one this is, and of how many. Null for a
   * document in one file. While the document is being produced, `parts` is an
   * estimate on the first file, so the waiting note can say how many are coming.
   */
  part: number | null;
  parts: number | null;
  /** The first file's id, on every later file of the same document. */
  partOf: string | null;
  /** How its share links stand. */
  sharing: { active: number; total: number; views: number; lastViewedAt: string | null };
}

/** The design assistant's allowance, as a share and a count. Never money. */
export interface AiAllowanceView {
  /** Off for everybody, or not configured: the designer shows no AI panel. */
  enabled: boolean;
  /** 0..1 of this month's allowance used. */
  used: number;
  /** Approximately how many more generations fit, by the formula in `shared/cloud-plans.json`. */
  approxLeft: number;
  /** ISO date the allowance starts again. */
  resetsOn: string;
  plan: string;
  upgradeTo?: string;
}

export type AiTemplateAnswer =
  | { status: "proposed"; requestId: string; template: ReportTemplate; notes?: string }
  | { status: "refused"; requestId: string; reason: string };

/* ---------------------------------- calls ---------------------------------- */

export const api = {
  workspace: () => get<WorkspaceView>("workspace"),
  /**
   * Creates the workspace behind a business name.
   *
   * `clerkOrgId` comes back because the token in this browser was minted before
   * the organisation existed, and Clerk needs to be told to make it active before
   * the next call will resolve to anything.
   */
  createWorkspace: (name: string, source?: string | null) =>
    post<WorkspaceView & { clerkOrgId: string; tokenStale?: boolean }>("workspace", source ? { name, source } : { name }),
  /**
   * Brings the workspace and its Clerk organisation back into step: the plan's
   * rules onto the organisation, and a name changed in Clerk's screen onto the
   * workspace. People calls it on opening and whenever Clerk says something changed.
   */
  settleOrganisation: () => post<WorkspaceView>("workspace/organisation"),
  /** An invitation whose link comes back to Cloud rather than to the instance's fallback. Admins only. */
  invite: (body: { emailAddress: string; role: "member" | "admin" }) => post<{ invited: string }>("workspace/invitations", body),

  folders: () => get<{ folders: FolderRow[] }>("folders"),
  createFolder: (body: { section: string; name: string; parentId?: string | null }) => post<{ folder: FolderRow }>("folders", body),
  updateFolder: (id: string, body: { name?: string; parentId?: string | null }) => patch<{ folder: FolderRow }>(`folders/${id}`, body),
  /** Nothing inside is deleted: it all moves up one level, and the reply says how much. */
  deleteFolder: (id: string) =>
    del<{ deleted: string; itemsMoved: number; foldersMoved: number; movedTo: string | null }>(`folders/${id}`),

  brands: () => get<{ brands: Brand[]; looks: Look[] }>("brands"),
  createBrand: (body: Partial<Omit<Brand, "id" | "updatedAt">> & { name: string }) => post<{ brand: Brand }>("brands", body),
  updateBrand: (id: string, body: Partial<Omit<Brand, "id" | "updatedAt">>) => patch<{ brand: Brand }>(`brands/${id}`, body),
  deleteBrand: (id: string) => del<{ deleted: string; promoted: string | null }>(`brands/${id}`),

  datasets: () => get<{ datasets: DatasetSummary[] }>("datasets"),
  /** The rows themselves, a page at a time, formatted the way a document shows them. */
  datasetRows: (id: string, offset: number, limit = 50) =>
    get<{
      columns: Array<{ name: string; label: string; type: string }>;
      rows: Array<Record<string, string>>;
      offset: number;
      limit: number;
      total: number;
    }>(`datasets/${id}/rows?offset=${offset}&limit=${limit}`),
  dataset: (id: string) => get<DatasetDetail>(`datasets/${id}`),
  /** Every column and every stored row, unformatted, for the grid that edits them. */
  datasetData: (id: string) =>
    get<{ dataset: DatasetDetail["dataset"]; columns: SourceColumn[]; rows: Array<Record<string, unknown>> }>(`datasets/${id}/data`),
  replaceDataset: (id: string, body: { name?: string; columns: SourceColumn[]; rows: Array<Record<string, unknown>> }) => put<DatasetDetail>(`datasets/${id}/rows`, body),
  createDataset: (body: { name: string; source: string; columns: SourceColumn[]; rows: Array<Record<string, unknown>>; headerRow?: boolean; link?: DatasetLink }) =>
    post<DatasetDetail>("datasets", body),
  /** The linked file read again in this tab and merged with `refreshDataset`. */
  refreshDataset: (id: string, body: { columns: SourceColumn[]; rows: Array<Record<string, unknown>>; link?: DatasetLink }) =>
    post<DatasetDetail>(`datasets/${id}/refresh`, body),
  /** `link: null` stops refreshing from the file; the rows stay as they are. */
  updateDataset: (id: string, body: { name?: string; columns?: SourceColumn[]; folderId?: string | null; link?: null }) => patch<DatasetDetail>(`datasets/${id}`, body),
  deleteDataset: (id: string) => del<{ deleted: string; designsAffected: string[] }>(`datasets/${id}`),

  starters: () => get<{ starters: StarterSummary[] }>("starters"),
  templates: () => get<{ templates: TemplateSummary[] }>("templates"),
  /** `group` picks which of a split design's documents the canvas shows. */
  template: (id: string, group?: string | null) =>
    get<TemplateDetail>(`templates/${id}${group ? `?group=${encodeURIComponent(group)}` : ""}`),
  createTemplate: (body: { name?: string; starterId?: string; datasetId?: string }) =>
    post<{ template: { id: string; name: string; starterId: string | null; datasetId: string | null } }>("templates", body),
  saveTemplate: (
    id: string,
    body: { name?: string; template?: ReportTemplate; datasetId?: string | null; mapping?: Mapping | null; brandId?: string | null; groupBy?: string | null; folderId?: string | null }
  ) =>
    put<{ template: { id: string; name: string }; savedAt: string }>(`templates/${id}`, body),
  deleteTemplate: (id: string) => del<{ deleted: string }>(`templates/${id}`),
  /**
   * Makes a design from a document read in this browser.
   *
   * The document itself is never sent: only the template and, when its main table
   * became data, that table's columns and rows. The service checks all of it again.
   */
  createImport: (body: {
    name: string;
    format: "docx" | "pdf";
    filename: string;
    template: ReportTemplate;
    dataset?: { name: string; columns: SourceColumn[]; rows: Array<Record<string, unknown>> };
    stats?: Record<string, unknown>;
  }) => post<{ template: { id: string; name: string }; datasetId: string | null }>("imports", body),
  matchTemplate: (id: string, body: { datasetId: string; mapping?: Mapping }) => post<MatchResult>(`templates/${id}/match`, body),

  reports: () => get<{ reports: ReportSummary[] }>("reports"),
  report: (id: string) => get<{ report: ReportSummary }>(`reports/${id}`),
  createReport: (body: { templateId: string; datasetId?: string; scope?: "separate" | "combined" | "one"; group?: string }) =>
    post<{ report: ReportSummary; reports: ReportSummary[] }>("reports", body),
  downloadReport: (id: string) => get<{ url: string; expiresInMinutes: number }>(`reports/${id}/download`),
  /** Renames the document in the list and on the next download; the file already saved is untouched. */
  updateReport: (id: string, body: { name?: string; folderId?: string | null }) =>
    patch<{ report: { id: string; name: string; folderId: string | null } }>(`reports/${id}`, body),
  /**
   * Deletes a document. `shared: true` says the person has been told its links will
   * stop working — without it, a document with live links is refused.
   */
  deleteReport: (id: string, opts: { shared?: boolean } = {}) => del<{ deleted: string }>(`reports/${id}${opts.shared ? "?shared=confirm" : ""}`),

  shares: (reportId: string) => get<{ shares: ShareView[] }>(`reports/${reportId}/shares`),
  createShare: (reportId: string, body: { label?: string | null; expiresAt?: string | null; password?: string | null }) =>
    post<{ share: ShareView }>(`reports/${reportId}/shares`, body),
  /** `null` removes a setting; leaving a key out leaves it alone. */
  updateShare: (id: string, body: { label?: string | null; expiresAt?: string | null; password?: string | null; stop?: true }) =>
    patch<{ share: ShareView }>(`shares/${id}`, body),
  deleteShare: (id: string) => del<{ deleted: string }>(`shares/${id}`),

  publicShare: (token: string) => anonymous<PublicShare>(`public/shares/${encodeURIComponent(token)}`),
  openShare: (token: string, password?: string) =>
    anonymous<{ document: SharedDocumentInfo; viewUrl: string; expiresInMinutes: number }>(`public/shares/${encodeURIComponent(token)}/open`, password ? { password } : {}),
  downloadShare: (token: string, password?: string) =>
    anonymous<{ url: string; expiresInMinutes: number }>(`public/shares/${encodeURIComponent(token)}/download`, password ? { password } : {}),

  revisions: (templateId: string, before?: number | null) => get<RevisionPage>(`templates/${templateId}/revisions${before ? `?before=${before}` : ""}`),
  revision: (templateId: string, number: number, compare: "previous" | "current" | number = "previous") =>
    get<RevisionDetail>(`templates/${templateId}/revisions/${number}?compare=${compare}`),
  restoreRevision: (templateId: string, number: number) =>
    post<{ template: { id: string; name: string }; revision: number | null; restoredFrom: number; unchanged: boolean }>(`templates/${templateId}/revisions/${number}/restore`),

  aiAllowance: () => get<AiAllowanceView>("ai/allowance"),
  /**
   * One design from the assistant. The service checks what the model returns and
   * asks it again itself when it fails, so what comes back has passed the same
   * validation any saved design has — and the tab checks it once more.
   */
  aiTemplate: (request: TemplateAiRequest, signal: AbortSignal) =>
    call<AiTemplateAnswer>("ai/template", { method: "POST", body: JSON.stringify({ request }), signal }),
  /** Whether the person applied what was proposed. Recorded against the generation, never used to decide anything. */
  aiDecision: (requestId: string, decision: "applied" | "rejected") =>
    post<{ recorded: boolean }>(`ai/generations/${encodeURIComponent(requestId)}/decision`, { decision }),

  checkout: (plan: string, cadence: "monthly" | "annual") => post<{ url: string }>("billing/checkout", { plan, cadence }),
  portal: () => post<{ url: string }>("billing/portal"),

  closure: () => get<ClosurePreview>("workspace/closure"),
  deleteWorkspace: (body: { confirmName: string; cancelSubscription?: boolean; acknowledgeOtherOrganisations?: boolean }) =>
    call<{ deleted: true; subscriptionCancelled: boolean; signInRemoved: boolean }>("workspace", { method: "DELETE", body: JSON.stringify(body) })
};

/**
 * Waits for a document to be produced.
 *
 * Polls, with the interval widening: a render usually finishes in under a second,
 * so asking quickly at first costs one extra request and saves the user a wait they
 * would otherwise notice. It stops widening at two seconds, and gives up after
 * three minutes with a message rather than spinning for ever.
 */
export async function waitForReport(id: string, onStatus?: (report: ReportSummary) => void): Promise<ReportSummary> {
  const started = Date.now();
  let wait = 400;
  for (;;) {
    const { report } = await api.report(id);
    onStatus?.(report);
    if (report.status === "ready" || report.status === "failed") return report;
    // A document coming as several files is allowed minutes a file: fifty thousand
    // rows is honestly a long render, and giving up at three minutes would tell
    // somebody it had failed while it was still being made.
    const patience = 180_000 + Math.max(0, (report.parts ?? 1) - 1) * 5 * 60_000;
    if (Date.now() - started > patience) {
      throw new ApiError("unavailable", "This document is taking much longer than usual.", "It is still being worked on — check the Documents list in a minute.");
    }
    await new Promise((r) => setTimeout(r, wait));
    wait = Math.min(2000, Math.round(wait * 1.4));
  }
}
